import React, { useState } from 'react';
import { Modal, Box, Typography, Button, IconButton, TextField, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchResultGrid from '../searchResultGrid/searchResultGrid';
import modalStyle from '../../styles/modalStyle';
import getCoverageColDef from '../../colDef/coverageColumnDef';
import Coverage from '../../interfaces/coverage';
import CoverageService from '../../services/coverageService';

interface BulkDeleteCoverageModalProps {
    open: boolean;
    onClose: () => void;
    selectedRows: Coverage[];
    onSuccess: () => void;
}

const BulkDeleteCoverageModal: React.FC<BulkDeleteCoverageModalProps> = ({ open, onClose, selectedRows, onSuccess }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [deleteComment, setDeleteComment] = useState<string>('');

    const handleSubmit = async () => {
        if (!deleteComment.trim()) {
            alert("Please enter a comment for deletion.");
            return;
        }

        try {
            setLoading(true);
            const promises = selectedRows.map(coverage => 
                CoverageService.deactivateCoverage(coverage.id, {
                    comment: deleteComment.trim()
                })
            );
            
            await Promise.all(promises);
            alert(`Successfully deleted ${selectedRows.length} coverage${selectedRows.length > 1 ? 's' : ''}`);
            onClose();
            onSuccess();
        } catch (error) {
            console.error('Error deleting coverage: ', error);
            alert("Failed to delete coverage");
        } finally {
            setLoading(false);
            onClose();
        }
    };

    const handleClose = () => {
        setDeleteComment('');
        onClose();
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                {loading ? (
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                        <CircularProgress size={50} />
                    </Box>
                ) : (
                    <>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6">Delete Coverage</Typography>
                            <IconButton onClick={handleClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
                            Are you sure you want to delete {selectedRows.length} coverage{selectedRows.length > 1 ? 's' : ''}?
                        </Typography>
                        <TextField
                            label="Comment (Required)"
                            value={deleteComment}
                            onChange={(e) => setDeleteComment(e.target.value)}
                            fullWidth
                            required
                            multiline
                            rows={3}
                            placeholder="Enter reason for deletion"
                            sx={{ mb: 2 }}
                            error={!deleteComment.trim()}
                            helperText={!deleteComment.trim() ? "Comment is required for deletion" : ""}
                        />
                        <Box mt={3}>
                            <Typography variant="subtitle1">Selected Rows:</Typography>
                            <SearchResultGrid
                                getRowId={(row: Coverage) => row.id}
                                results={selectedRows}
                                onSelectRow={() => {}}
                                columns={getCoverageColDef()}
                                isRowSelectionAllowed={false}
                            />
                        </Box>
                        <Box sx={{ mt: 3 }} display="flex" justifyContent="flex-end" gap={1}>
                            <Button variant="outlined" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button 
                                variant="contained" 
                                color="error" 
                                onClick={handleSubmit}
                                disabled={!deleteComment.trim()}
                            >
                                Confirm
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default BulkDeleteCoverageModal;
